/**
 * Plain-text Bible verse boundaries and numbering helpers (API.bible text after HTML strip).
 * Used by passage highlight rendering and the chapter verse reader.
 */

/** Indices in `text` where a verse number begins (1–200), for paragraph spacing between verses. */
export function collectVerseNumberStartIndices(text) {
  if (!text) return [];
  const starts = new Set();
  const add = (idx, raw) => {
    const n = parseInt(raw, 10);
    if (n >= 1 && n <= 200) starts.add(idx);
  };
  let m;
  const reLine = /^(\d{1,3})(\s)/gm;
  while ((m = reLine.exec(text)) !== null) add(m.index, m[1]);
  const reAfterNl = /\n(\d{1,3})(\s)/g;
  while ((m = reAfterNl.exec(text)) !== null) add(m.index + 1, m[1]);
  const reSpace = /(\s)(\d{1,3})(?=\s+[A-Za-z])/g;
  while ((m = reSpace.exec(text)) !== null) add(m.index + m[1].length, m[2]);
  const rePunct = /([\.\,\;\:\!\?])(\s+)(\d{1,3})(\s+[A-Z])/g;
  while ((m = rePunct.exec(text)) !== null) add(m.index + m[1].length + m[2].length, m[3]);
  const reGlue = /([a-z\.\!\?\:;,])(\d{1,3})(\s+)(?=[A-Z])/gi;
  while ((m = reGlue.exec(text)) !== null) add(m.index + m[1].length, m[2]);
  return [...starts].sort((a, b) => a - b);
}

/** Non-overlapping [start, end) ranges covering `text`, split at verse boundaries. */
export function getVerseSegments(text) {
  if (!text) return [];
  const starts = collectVerseNumberStartIndices(text);
  if (!starts.length) return [{ start: 0, end: text.length }];
  const segments = [];
  if (starts[0] > 0) segments.push({ start: 0, end: starts[0] });
  for (let i = 0; i < starts.length; i++) {
    const end = i + 1 < starts.length ? starts[i + 1] : text.length;
    segments.push({ start: starts[i], end });
  }
  return segments;
}

/** Verse numbers in a page chunk — for Prev/Next button labels (reading view). */
export function extractVerseNumbersFromPageChunk(s) {
  if (!s || typeof s !== "string") return [];
  const nums = new Set();
  const add = (raw) => {
    const v = parseInt(raw, 10);
    if (v >= 1 && v <= 176) nums.add(v);
  };
  let m;
  const r1 = /^(\d{1,3})(\s)/gm;
  while ((m = r1.exec(s)) !== null) add(m[1]);
  const r2 = /(\s)(\d{1,3})(?=\s+[A-Za-z])/g;
  while ((m = r2.exec(s)) !== null) add(m[2]);
  const r3 = /([\;,])(\d{1,3})(?=\s)/g;
  while ((m = r3.exec(s)) !== null) add(m[2]);
  const r4 = /([\.\!\?\:])(\s+)(\d{1,3})(?=\s+[A-Z])/g;
  while ((m = r4.exec(s)) !== null) add(m[3]);
  const r5 = /([a-z])(\d{1,3})(?=\s+[A-Z])/gi;
  while ((m = r5.exec(s)) !== null) add(m[2]);
  return [...nums].sort((a, b) => a - b);
}

/** e.g. "Ezra 1" + [1,2,3] → "Ezra 1:1–3" */
export function formatChapterVerseRef(passageLabel, verseNums) {
  const base = (passageLabel || "").trim();
  if (!verseNums.length) return base || "—";
  const min = verseNums[0];
  const max = verseNums[verseNums.length - 1];
  if (!base) return min === max ? `${min}` : `${min}–${max}`;
  if (min === max) return `${base}:${min}`;
  return `${base}:${min}–${max}`;
}

/**
 * Read verse number at segment start (plain text after verse boundary).
 * @returns {number|null} 1–200 or null if not a verse start
 */
export function readVerseNumberAt(text, startIdx) {
  if (!text || startIdx < 0 || startIdx >= text.length) return null;
  let i = startIdx;
  while (i < text.length && /[\s\n\r]/.test(text[i])) i++;
  const rest = text.slice(i, i + 12);
  const m = rest.match(/^(\d{1,3})(?=\s)/);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  if (n < 1 || n > 200) return null;
  return n;
}

/**
 * Remove leading verse number from a segment slice when we show the number separately.
 */
export function stripLeadingVerseNumber(slice, verseNum) {
  if (verseNum == null || !slice) return slice;
  const re = new RegExp(`^\\s*${verseNum}\\s+`);
  return slice.replace(re, "");
}

/**
 * Rows for chapter reader: one entry per getVerseSegments slice with optional verse number.
 * @returns {{ verseNum: number|null, body: string, start: number, end: number }[]}
 */
export function buildChapterVerseRows(fullText) {
  if (!fullText) return [];
  const segments = getVerseSegments(fullText);
  return segments.map((seg) => {
    const slice = fullText.slice(seg.start, seg.end);
    const verseNum = readVerseNumberAt(fullText, seg.start);
    const body = verseNum != null ? stripLeadingVerseNumber(slice, verseNum) : slice;
    return {
      verseNum,
      body,
      start: seg.start,
      end: seg.end,
    };
  });
}
